/**
 * Saga level map shared by every level-based arcade game: a winding road of levels grouped in
 * themed chapters, best stars per level, the next level pinned and one tap from play.
 *
 *   index.ts:  scenes: [levelMapScene({ id, gameScene, title, accent, describe }), GameScene]
 *   game win:  openMap(this, id, { from: n, result: "won", ...recordWin(id, n, stars) })
 *
 * H1/H5: no lives, keys, timers or paid unlocks. Clearing a level (or skipping it for free after
 * three losses, H4) opens the next one; every open level can be replayed forever.
 * Performance (A14 budget): ground, decorations and road are drawn once per chapter into a canvas
 * texture; only nodes, the pin and the level card are live objects, and off-screen nodes are culled.
 */
import Phaser from "phaser";
import { designRole } from "@enjoyment/core";
import { sfx, unlockAudio } from "@shared/audio";
import { pageAccount } from "./account";
import { accountButton } from "./accountButton";
import { exposeBotHook } from "./botHook";
import { currentLang, pick, type Lang } from "./lang";
import { BG, FONT, PANEL, PANEL_2, TEXT_DIM, W, button, centerCamera, confetti, text } from "./ui";
import {
  CHAPTER_SIZE,
  chapterOf,
  chapterRange,
  chapterStars,
  lastShownLevel,
  loadProgress,
  nodeState,
  recordWin,
  registerLegacyLevel,
  starsOf,
  totalStars,
  type NodeState,
  type Progress,
  type WinOutcome,
} from "./progress";
import { NODE_R, chapterBand, chapterBannerY, hash01, nodePoint, roadX } from "./mapLayout";

export type Deco = "meadow" | "forest" | "lake" | "desert" | "canyon" | "snow" | "garden" | "night";
export interface ChapterTheme {
  tr: string;
  en: string;
  ground: number;
  ground2: number;
  deco: Deco;
}

/** Chapters cycle through these lands; names are scenery only, never a promise about content. */
export const CHAPTER_THEMES: ChapterTheme[] = [
  { tr: "Çayır", en: "Meadow", ground: 0x1d3a2c, ground2: 0x285239, deco: "meadow" },
  { tr: "Orman", en: "Forest", ground: 0x132d20, ground2: 0x1c402c, deco: "forest" },
  { tr: "Göl Kıyısı", en: "Lakeside", ground: 0x15304a, ground2: 0x1e4566, deco: "lake" },
  { tr: "Çöl", en: "Desert", ground: 0x3e2d1d, ground2: 0x5a4229, deco: "desert" },
  { tr: "Kanyon", en: "Canyon", ground: 0x3a1f24, ground2: 0x56313a, deco: "canyon" },
  { tr: "Karlı Tepeler", en: "Snowy Peaks", ground: 0x253350, ground2: 0x35486e, deco: "snow" },
  { tr: "Çiçek Bahçesi", en: "Flower Garden", ground: 0x2f1f3a, ground2: 0x452d54, deco: "garden" },
  { tr: "Yıldızlı Gece", en: "Starry Night", ground: 0x151438, ground2: 0x232054, deco: "night" },
];

const STRINGS = {
  games: { tr: "‹ Oyunlar", en: "‹ Games" },
  map: { tr: "‹ Harita", en: "‹ Map" },
  level: { tr: "Seviye", en: "Level" },
  play: { tr: "Oyna", en: "Play" },
  replay: { tr: "Tekrar oyna", en: "Play again" },
  close: { tr: "Kapat", en: "Close" },
  chapter: { tr: "BÖLÜM", en: "CHAPTER" },
  stars: { tr: "YILDIZ", en: "STARS" },
  locked: { tr: "Kilitli · önce Seviye {n}", en: "Locked · clear level {n} first" },
  skipped: { tr: "Ücretsiz atlandı · dönüp yıldız toplayabilirsin", en: "Skipped for free · come back for the stars" },
  fresh: { tr: "Yeni seviye", en: "New level" },
  perfect: { tr: "Üç yıldız · kusursuz", en: "Three stars · perfect" },
  more: { tr: "Daha çok yıldız için tekrar oyna", en: "Play again for more stars" },
  chapterDone: { tr: "Bölüm tamam!", en: "Chapter complete!" },
  continue: { tr: "Devam", en: "Continue" },
  free: { tr: "Can yok · bekleme yok · her seviye ücretsiz tekrar", en: "No lives · no waiting · replay any level for free" },
  hard: { tr: "ZOR", en: "HARD" },
  boss: { tr: "BÖLÜM SONU", en: "FINALE" },
  hardCard: { tr: "Zor seviye · sonrası daha rahat", en: "Hard level · an easier one follows" },
  bossCard: { tr: "Bölüm sonu · bölümün en zoru", en: "Chapter finale · the hardest of the chapter" },
} as const;
export type MapTextKey = keyof typeof STRINGS;
export function mapText(key: MapTextKey, lang: Lang = currentLang()): string {
  return pick(lang, STRINGS[key]);
}

export interface LevelMapConfig {
  /** Save id (`arcade.<id>.map`). */
  id: string;
  /** Scene key of the game; it is started with `{ level }`. */
  gameScene: string;
  title: { tr: string; en: string };
  accent: number;
  background?: number;
  chapterSize?: number;
  maxLevel?: number;
  /** One short line for the level card — the goal of level n. Runs on tap, keep it cheap. */
  describe?: (level: number, lang: Lang) => string;
  chapters?: ChapterTheme[];
  /** Index into the chapter themes for chapter 1, so every game's road starts somewhere of its own. */
  firstTheme?: number;
  /** The game's telemetry (`map_open`, `map_play`); the map imports no analytics of its own. */
  track: (name: "map_open" | "map_play", props: Record<string, string | number | boolean>) => void;
  /** Where "‹ …" in the map's top bar leads. Default: the Enjoyment hub ("‹ Oyunlar"). */
  back?: { label: { tr: string; en: string }; go: (scene: Phaser.Scene) => void };
  /** Language of the map. Default: `currentLang()` (?lang → launcher preference → browser). */
  lang?: () => Lang;
  /** Next level from the game's own pre-map save, migrated once (default: `arcade.<id>.level`). */
  legacyLevel?: () => number | undefined;
}

/** What a game hands back to the map after a level, so the map can celebrate it. */
export interface MapReturn {
  from?: number;
  result?: "won" | "skip";
  stars?: number;
  prevStars?: number;
  unlockedNext?: boolean;
}

export const mapKey = (id: string): string => `${id}-map`;

/** Map hand-off after `recordWin(id, n, stars)`. */
export function afterWin(from: number, o: WinOutcome): MapReturn {
  return { from, result: "won", stars: o.stars, prevStars: o.prevStars, unlockedNext: o.unlockedNext };
}

/** Map hand-off after `recordSkip(id, n)` (H4). */
export function afterSkip(from: number, o: { unlockedNext: boolean }): MapReturn {
  return { from, result: "skip", unlockedNext: o.unlockedNext };
}

/** Leave a level for the map. Without a registered map (embedded use) it simply opens the next level. */
export function openMap(scene: Phaser.Scene, id: string, data: MapReturn = {}): void {
  if (scene.scene.manager.keys[mapKey(id)]) scene.scene.start(mapKey(id), data);
  else scene.scene.restart({ level: data.result ? (data.from ?? 0) + 1 : data.from });
}

/** Back link for the kit `topBar()`: "‹ Harita" → the game's level map. */
export function mapBack(scene: Phaser.Scene, id: string): { label: string; onClick: () => void } {
  return { label: mapText("map"), onClick: () => openMap(scene, id) };
}

/** "‹ Harita" link for a game's own top bar. */
export function mapBackLink(scene: Phaser.Scene, id: string, x: number, y: number, color = TEXT_DIM, size = 22): Phaser.GameObjects.Text {
  return scene.add
    .text(x, y, mapText("map"), { fontFamily: FONT, fontSize: `${size}px`, color })
    .setOrigin(0, 0.5)
    .setInteractive({ useHandCursor: true })
    .on("pointerdown", (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
      ev.stopPropagation();
      openMap(scene, id);
    });
}

/** "★★☆" for end-of-level cards. */
export function starLine(stars: number): string {
  const n = Math.max(0, Math.min(3, Math.floor(stars)));
  return "★".repeat(n) + "☆".repeat(3 - n);
}

// ------------------------------------------------------------------ drawing helpers

const GOLD = 0xffd23f;
const ROAD = 0xf2e6c8;
const ROAD_LOCKED = 0x4d5279;
const TOP_H = 150;
/** The pin floats this far above the current level, clear of its stars. */
const PIN_DY = 118;
const BOTTOM_H = 210;

function shade(c: number, f: number): number {
  const ch = (s: number) => Math.max(0, Math.min(255, Math.round(((c >> s) & 255) * f)));
  return (ch(16) << 16) | (ch(8) << 8) | ch(0);
}

function isLight(c: number): boolean {
  return (0.299 * ((c >> 16) & 255) + 0.587 * ((c >> 8) & 255) + 0.114 * (c & 255)) / 255 > 0.62;
}

function starPoints(x: number, y: number, r: number): Phaser.Math.Vector2[] {
  const pts: Phaser.Math.Vector2[] = [];
  for (let i = 0; i < 10; i++) {
    const a = -Math.PI / 2 + (i * Math.PI) / 5;
    const rr = i % 2 ? r * 0.45 : r;
    pts.push(new Phaser.Math.Vector2(x + Math.cos(a) * rr, y + Math.sin(a) * rr));
  }
  return pts;
}

function drawStar(g: Phaser.GameObjects.Graphics, x: number, y: number, r: number, filled: boolean): void {
  const pts = starPoints(x, y, r);
  if (filled) {
    g.fillStyle(0x7a5300, 0.55).fillPoints(starPoints(x, y + 3, r), true);
    g.fillStyle(GOLD, 1).fillPoints(pts, true);
    g.fillStyle(0xffffff, 0.35).fillPoints(starPoints(x - r * 0.12, y - r * 0.16, r * 0.42), true);
  } else {
    g.fillStyle(0x000000, 0.4).fillPoints(pts, true);
    g.lineStyle(2, 0xffffff, 0.28).strokePoints(pts, true);
  }
}

/** One decoration at (x, y), scale s, variant k ∈ [0,1). Drawn once into a chapter texture. */
function drawDeco(g: Phaser.GameObjects.Graphics, kind: Deco, t: ChapterTheme, x: number, y: number, s: number, k: number): void {
  switch (kind) {
    case "meadow": {
      const c = k < 0.5 ? 0x2f6b45 : 0x3a7d4f;
      g.fillStyle(0x000000, 0.18).fillEllipse(x + 4, y + 14 * s, 64 * s, 16 * s);
      g.fillStyle(shade(c, 0.8), 1).fillCircle(x - 14 * s, y, 15 * s).fillCircle(x + 14 * s, y + 2 * s, 14 * s);
      g.fillStyle(c, 1).fillCircle(x, y - 8 * s, 18 * s);
      if (k > 0.35) g.fillStyle(k > 0.7 ? 0xffe08a : 0xff8fab, 1).fillCircle(x - 6 * s, y - 14 * s, 3.5 * s).fillCircle(x + 9 * s, y - 4 * s, 3 * s);
      break;
    }
    case "forest": {
      const c = k < 0.5 ? 0x1f5a37 : 0x174a2d;
      g.fillStyle(0x000000, 0.2).fillEllipse(x + 3, y + 4 * s, 46 * s, 12 * s);
      g.fillStyle(0x4a3120, 1).fillRect(x - 4 * s, y - 10 * s, 8 * s, 14 * s);
      g.fillStyle(shade(c, 0.85), 1).fillTriangle(x, y - 46 * s, x - 26 * s, y - 6 * s, x + 26 * s, y - 6 * s);
      g.fillStyle(c, 1).fillTriangle(x, y - 70 * s, x - 20 * s, y - 32 * s, x + 20 * s, y - 32 * s);
      g.fillStyle(0xffffff, 0.08).fillTriangle(x, y - 70 * s, x - 20 * s, y - 32 * s, x - 4 * s, y - 32 * s);
      break;
    }
    case "lake": {
      if (k < 0.45) {
        g.fillStyle(0x0d2238, 0.6).fillEllipse(x, y + 4 * s, 130 * s, 50 * s);
        g.fillStyle(0x2d6e9e, 0.75).fillEllipse(x, y, 120 * s, 42 * s);
        g.lineStyle(2, 0xa8d8ff, 0.35).strokeEllipse(x - 14 * s, y - 4 * s, 50 * s, 12 * s).strokeEllipse(x + 22 * s, y + 6 * s, 34 * s, 8 * s);
      } else {
        g.lineStyle(3 * s, 0x6f9a5a, 0.9);
        for (let i = -2; i <= 2; i++) g.lineBetween(x + i * 7 * s, y, x + i * 9 * s, y - (26 + (i % 2) * 10) * s);
        g.fillStyle(0x8a5a32, 1).fillEllipse(x + 9 * s, y - 34 * s, 5 * s, 12 * s);
      }
      break;
    }
    case "desert": {
      if (k < 0.5) {
        g.fillStyle(shade(t.ground2, 1.15), 1).fillEllipse(x, y, 150 * s, 40 * s);
        g.fillStyle(t.ground, 1).fillRect(x - 80 * s, y, 160 * s, 24 * s);
      } else {
        const c = 0x4f8a4a;
        g.fillStyle(0x000000, 0.18).fillEllipse(x + 4, y + 2 * s, 40 * s, 10 * s);
        g.fillStyle(c, 1).fillRoundedRect(x - 8 * s, y - 56 * s, 16 * s, 58 * s, 8 * s);
        g.fillRoundedRect(x - 26 * s, y - 40 * s, 10 * s, 24 * s, 5 * s).fillRect(x - 26 * s, y - 22 * s, 20 * s, 8 * s);
        g.fillRoundedRect(x + 16 * s, y - 46 * s, 10 * s, 22 * s, 5 * s).fillRect(x + 6 * s, y - 30 * s, 20 * s, 8 * s);
        g.fillStyle(0xffffff, 0.12).fillRoundedRect(x - 6 * s, y - 54 * s, 5 * s, 52 * s, 3 * s);
      }
      break;
    }
    case "canyon": {
      const c = k < 0.5 ? 0x7a3b33 : 0x8f4b3c;
      g.fillStyle(0x000000, 0.22).fillEllipse(x + 4, y + 2 * s, 70 * s, 14 * s);
      g.fillStyle(shade(c, 0.8), 1).fillRoundedRect(x - 26 * s, y - 64 * s, 52 * s, 66 * s, 10 * s);
      g.fillStyle(c, 1).fillRoundedRect(x - 26 * s, y - 64 * s, 34 * s, 66 * s, 10 * s);
      g.fillStyle(0xffffff, 0.1).fillRect(x - 22 * s, y - 44 * s, 44 * s, 4 * s).fillRect(x - 22 * s, y - 24 * s, 44 * s, 4 * s);
      break;
    }
    case "snow": {
      if (k < 0.6) {
        g.fillStyle(0x000000, 0.2).fillEllipse(x + 3, y + 4 * s, 46 * s, 12 * s);
        g.fillStyle(0x1f4a3f, 1).fillTriangle(x, y - 66 * s, x - 26 * s, y, x + 26 * s, y);
        g.fillStyle(0xeef4ff, 1).fillTriangle(x, y - 66 * s, x - 13 * s, y - 33 * s, x + 13 * s, y - 33 * s);
        g.fillStyle(0xeef4ff, 0.8).fillEllipse(x, y + 2 * s, 54 * s, 10 * s);
      } else {
        g.fillStyle(0xffffff, 0.55).fillCircle(x, y, 3 * s).fillCircle(x + 22 * s, y - 16 * s, 2 * s).fillCircle(x - 18 * s, y - 28 * s, 2.5 * s);
      }
      break;
    }
    case "garden": {
      const petal = [0xff8fab, 0xffc857, 0xb69cff, 0x7fd6ff][Math.floor(k * 4)]!;
      g.lineStyle(3 * s, 0x3f7d4a, 1).lineBetween(x, y, x, y - 28 * s);
      g.fillStyle(0x3f7d4a, 1).fillEllipse(x + 8 * s, y - 12 * s, 14 * s, 6 * s);
      for (let i = 0; i < 5; i++) {
        const a = (i * Math.PI * 2) / 5;
        g.fillStyle(petal, 1).fillCircle(x + Math.cos(a) * 9 * s, y - 36 * s + Math.sin(a) * 9 * s, 7 * s);
      }
      g.fillStyle(0xffe08a, 1).fillCircle(x, y - 36 * s, 5 * s);
      break;
    }
    case "night": {
      if (k < 0.12) {
        g.fillStyle(0xfff3c4, 0.9).fillCircle(x, y, 30 * s);
        g.fillStyle(t.ground, 1).fillCircle(x + 13 * s, y - 8 * s, 26 * s);
      } else {
        g.fillStyle(0xfff3c4, 0.35 + k * 0.5).fillPoints(starPoints(x, y, (5 + k * 6) * s), true);
        g.fillStyle(0xffffff, 0.4).fillCircle(x + 26 * s, y + 14 * s, 1.8 * s);
      }
      break;
    }
  }
}

// ------------------------------------------------------------------ scene factory

export function levelMapScene(cfg: LevelMapConfig): typeof Phaser.Scene {
  const size = cfg.chapterSize ?? CHAPTER_SIZE;
  const themes = cfg.chapters ?? CHAPTER_THEMES;
  const theme = (chapter: number): ChapterTheme => themes[(chapter + (cfg.firstTheme ?? 0)) % themes.length]!;
  const onAccent = isLight(cfg.accent) ? "#1b1f3a" : "#ffffff";
  const track = cfg.track;
  if (cfg.legacyLevel) registerLegacyLevel(cfg.id, cfg.legacyLevel);
  let firstBoot = true;

  return class LevelMapScene extends Phaser.Scene {
    private lang: Lang = currentLang();
    private progress!: Progress;
    /** What is drawn; lags behind `progress` while the unlock is being celebrated. */
    private view!: Progress;
    private ret: MapReturn = {};
    private world!: Phaser.GameObjects.Container;
    private overlayRoad!: Phaser.GameObjects.Graphics;
    private spline!: Phaser.Curves.Spline;
    private splineCount = 2;
    private nodes = new Map<number, Phaser.GameObjects.Container>();
    private pin?: Phaser.GameObjects.Container;
    private pulse?: Phaser.GameObjects.Arc;
    private card?: Phaser.GameObjects.Container;
    private cardLevel = 0;
    private cardPlay?: Phaser.GameObjects.Container;
    private playBtn!: Phaser.GameObjects.Container;
    private bottomBg!: Phaser.GameObjects.Rectangle;
    private footer!: Phaser.GameObjects.Text;
    private starTotal!: Phaser.GameObjects.Text;
    private cloud!: Phaser.GameObjects.Container;
    private viewH = 1280;
    private lo = 0;
    private hi = 0;
    private last = 1;
    private dragging = false;
    private moved = false;
    private downY = 0;
    private startScroll = 0;
    private lastY = 0;
    private lastT = 0;
    private vel = 0;
    private busy = false;

    constructor() {
      super(mapKey(cfg.id));
    }

    init(data?: MapReturn): void {
      this.lang = cfg.lang?.() ?? currentLang();
      this.ret = data ?? {};
      this.nodes = new Map();
      this.pin = undefined;
      this.pulse = undefined;
      this.card = undefined;
      this.cardPlay = undefined;
      this.dragging = false;
      this.moved = false;
      this.vel = 0;
      this.busy = false;
    }

    create(): void {
      const q = new URLSearchParams(location.search);
      const deepLevel = Number(q.get("level"));
      const boot = firstBoot;
      firstBoot = false;
      // Migrate the game's pre-map save now: a level opened by a shared link rewrites that save on a win.
      loadProgress(cfg.id);
      // Bots and smoke tests start in the level (as before the map); `?map=1` keeps them here.
      // A shared `?level=N` link opens that level directly, once.
      if (boot && !q.has("map") && (q.has("bot") || (Number.isInteger(deepLevel) && deepLevel > 0))) {
        this.scene.start(cfg.gameScene, Number.isInteger(deepLevel) && deepLevel > 0 ? { level: deepLevel } : undefined);
        return;
      }
      // Smoke tests on slow software GL (`?bot=1&fast=4`): Phaser caps a frame's delta at 200 ms, so at
      // ~1 FPS the celebration would take minutes. Speeds up only this scene's tweens and timers.
      const fast = q.has("bot") ? Number(q.get("fast")) : 1;
      this.tweens.timeScale = this.time.timeScale = fast > 1 ? fast : 1;
      centerCamera(this);
      this.cameras.main.setBackgroundColor(cfg.background ?? BG);
      this.viewH = this.scale.height / this.cameras.main.zoom;
      this.progress = loadProgress(cfg.id);
      const r = this.ret;
      const celebrateUnlock = !!r.from && !!r.unlockedNext && this.progress.unlocked === r.from + 1;
      this.view = celebrateUnlock ? { ...this.progress, unlocked: r.from! } : this.progress;
      this.last = lastShownLevel(this.progress, size, cfg.maxLevel ?? Infinity);

      this.world = this.add.container(0, 0);
      this.buildSpline();
      this.drawChapters();
      this.overlayRoad = this.add.graphics();
      this.world.add(this.overlayRoad);
      // The level just played is drawn as it was before the result; celebrate() then updates it.
      const before = (n: number) => (r.from === n && r.result ? { state: nodeState(this.view, n), stars: Math.min(r.prevStars ?? 0, starsOf(this.progress, n)) } : undefined);
      for (let n = 1; n <= this.last; n++) this.makeNode(n, before(n));
      this.placeMarker(this.view.unlocked, false);
      this.drawChrome();
      this.bounds();
      this.world.y = this.scrollFor(r.from ?? this.view.unlocked);
      this.cull();

      this.input.on("pointerdown", this.onDown, this);
      this.input.on("pointermove", this.onMove, this);
      this.input.on("pointerup", this.onUp, this);
      this.input.on("pointerupoutside", this.onUp, this);
      this.input.on("wheel", (_p: Phaser.Input.Pointer, _o: unknown, _dx: number, dy: number) => this.scrollTo(this.world.y - dy));
      this.input.keyboard?.on("keydown", this.onKey, this);
      this.scale.on("resize", this.relayout, this);
      this.events.once("shutdown", () => {
        this.scale.off("resize", this.relayout, this);
        this.input.keyboard?.off("keydown", this.onKey, this);
        // Free the chapter textures (~5 MB each) for the level that is about to load.
        for (let c = 0; c <= chapterOf(this.last, size); c++) this.textures.remove(`map-${cfg.id}-c${c}`);
      });

      // Cloud saves: progress from another device that this sync brought in redraws the road (when nothing is moving).
      const account = pageAccount();
      if (account) {
        const off = account.onSavesChanged((games) => {
          if (games.includes(cfg.id) && this.sys.isActive() && !this.busy && !this.card) this.scene.restart({});
        });
        this.events.once("shutdown", off);
      }

      track("map_open", { unlocked: this.progress.unlocked, stars: totalStars(this.progress), from: r.from ?? 0 });
      exposeBotHook({
        state: () => this.botState(),
        play: () => this.play(this.progress.unlocked),
        // Smoke/video hook: pretend the current level was cleared and come back to celebrate it.
        win: (stars = 3) => {
          const n = this.progress.unlocked;
          this.scene.restart(afterWin(n, recordWin(cfg.id, n, Number(stars))));
        },
      });
      if (r.from && r.result && r.from <= this.progress.unlocked) void this.celebrate(r, celebrateUnlock);
    }

    override update(_t: number, delta: number): void {
      if (!this.world || this.dragging || Math.abs(this.vel) < 0.02) return;
      this.scrollTo(this.world.y + this.vel * delta);
      this.vel *= Math.pow(0.92, delta / 16.7);
      if (this.world.y <= this.lo || this.world.y >= this.hi) this.vel = 0;
    }

    // ---------------------------------------------------------------- layout

    private buildSpline(): void {
      const pts: Phaser.Math.Vector2[] = [];
      for (let n = 1; n <= this.last + 1; n++) {
        const p = nodePoint(n, size);
        pts.push(new Phaser.Math.Vector2(p.x, p.y));
      }
      this.splineCount = pts.length;
      this.spline = new Phaser.Curves.Spline(pts);
    }

    /** Point on the road at level-unit u (1 = level 1, 2.5 = halfway between levels 2 and 3). */
    private at(u: number): Phaser.Math.Vector2 {
      return this.spline.getPoint(Phaser.Math.Clamp((u - 1) / (this.splineCount - 1), 0, 1));
    }

    private roadPoints(a: number, b: number): Phaser.Math.Vector2[] {
      const out: Phaser.Math.Vector2[] = [];
      const steps = Math.max(2, Math.ceil((b - a) * 18));
      for (let k = 0; k <= steps; k++) out.push(this.at(a + ((b - a) * k) / steps));
      return out;
    }

    private strokeRoad(g: Phaser.GameObjects.Graphics, a: number, b: number, width: number, color: number, alpha: number): void {
      if (b <= a) return;
      const pts = this.roadPoints(a, b);
      g.lineStyle(width, color, alpha).strokePoints(pts);
      g.fillStyle(color, alpha);
      for (const p of pts) g.fillCircle(p.x, p.y, width / 2);
    }

    private drawRoadInto(g: Phaser.GameObjects.Graphics, done: number): void {
      const end = this.splineCount;
      this.strokeRoad(g, 1, end, 40, 0x000000, 0.22);
      this.strokeRoad(g, 1, end, 28, ROAD_LOCKED, 1);
      this.strokeRoad(g, 1, done, 28, ROAD, 1);
      for (let u = 1.1; u < end; u += 0.2) {
        const p = this.at(u);
        g.fillStyle(u < done ? 0xcdb98a : 0x3a3f63, 1).fillCircle(p.x, p.y, 3.5);
      }
    }

    /** Ground, scenery, banners and the road: one canvas texture per chapter (redrawn per visit). */
    private drawChapters(): void {
      const lastChapter = chapterOf(this.last, size);
      for (let c = 0; c <= lastChapter; c++) {
        const t = theme(c);
        let [top, bottom] = chapterBand(c, size);
        if (c === 0) bottom += 900;
        if (c === lastChapter) top -= 260;
        const h = Math.ceil(bottom - top);
        const g = this.make.graphics({}, false);
        g.translateCanvas(0, -top);
        g.fillStyle(t.ground, 1).fillRect(0, top, W, h);
        // rolling hills along both edges
        for (let i = 0; i < 9; i++) {
          const y = top + ((i + 0.5) / 9) * h;
          const left = i % 2 === 0;
          g.fillStyle(t.ground2, 0.85).fillEllipse(left ? 40 : W - 40, y, 260 + hash01(c, i) * 140, 170 + hash01(i, c) * 90);
        }
        g.fillStyle(shade(t.ground2, 1.12), 0.35).fillEllipse(W / 2, top + h * 0.5, W * 1.4, h * 0.25);
        // scenery, kept off the road
        const count = Math.round(h / 52);
        for (let i = 0; i < count; i++) {
          const y = top + 30 + hash01(c * 131 + i, 7) * (h - 60);
          const rx = roadX(y, size);
          let x = 24 + hash01(c * 131 + i, 13) * (W - 48);
          if (Math.abs(x - rx) < 130) x = rx > W / 2 ? rx - 140 - hash01(i, c + 3) * 110 : rx + 140 + hash01(i, c + 3) * 110;
          x = Phaser.Math.Clamp(x, 22, W - 22);
          drawDeco(g, t.deco, t, x, y, 0.75 + hash01(i, c * 17) * 0.55, hash01(i * 3, c));
        }
        this.drawRoadInto(g, this.view.unlocked);
        if (c === lastChapter) {
          for (let i = 0; i < 12; i++) g.fillStyle(cfg.background ?? BG, 1 - i / 12).fillRect(0, top + i * 22, W, 22);
        }
        const key = `map-${cfg.id}-c${c}`;
        if (this.textures.exists(key)) this.textures.remove(key);
        g.generateTexture(key, W, h);
        g.destroy();
        this.world.add(this.add.image(0, top, key).setOrigin(0, 0));
        // wide screens: plain ground beyond the 720 design column
        this.world.add(this.add.rectangle(-W, top, W, h, t.ground).setOrigin(0, 0));
        this.world.add(this.add.rectangle(W, top, W, h, t.ground).setOrigin(0, 0));
      }
      for (let c = 0; c <= lastChapter; c++) this.makeBanner(c);
    }

    private makeBanner(c: number): void {
      const y = chapterBannerY(c, size);
      const t = theme(c);
      const [first] = chapterRange(c, size);
      const open = first <= this.view.unlocked;
      const { got, max } = chapterStars(this.progress, c, size);
      const g = this.add.graphics();
      g.fillStyle(0x000000, 0.3).fillRoundedRect(-212, -34, 424, 80, 22);
      g.fillStyle(0x0d1024, 0.9).fillRoundedRect(-215, -40, 430, 80, 22);
      g.lineStyle(3, shade(t.ground2, 1.6), 0.9).strokeRoundedRect(-215, -40, 430, 80, 22);
      const label = this.add.text(0, -16, `${mapText("chapter", this.lang)} ${c + 1}${open ? `  ·  ★ ${got}/${max}` : ""}`, { fontFamily: FONT, fontSize: "16px", color: "#aab1dd", fontStyle: "bold" }).setOrigin(0.5);
      const name = this.add.text(0, 12, pick(this.lang, t), { fontFamily: FONT, fontSize: "28px", color: "#ffffff", fontStyle: "bold" }).setOrigin(0.5);
      const banner = this.add.container(W / 2, y, [g, label, name]).setName(`banner-${c}`).setAlpha(open ? 1 : 0.6);
      this.world.add(banner);
    }

    private makeNode(n: number, override?: { state: NodeState; stars: number }): Phaser.GameObjects.Container {
      this.nodes.get(n)?.destroy();
      const p = nodePoint(n, size);
      const st = override?.state ?? nodeState(this.view, n);
      const stars = override?.stars ?? starsOf(this.view, n);
      const fill = st === "done" ? cfg.accent : st === "skipped" ? 0x7a7fb0 : st === "current" ? 0xffffff : 0x343a60;
      const g = this.add.graphics();
      g.fillStyle(0x000000, 0.3).fillEllipse(0, 38, 92, 26);
      g.fillStyle(st === "locked" ? 0x23284a : shade(fill, 0.68), 1).fillCircle(0, 7, NODE_R);
      g.fillStyle(fill, 1).fillCircle(0, 0, NODE_R);
      g.fillStyle(0xffffff, st === "locked" ? 0.05 : 0.24).fillEllipse(0, -20, 58, 24);
      if (st === "current") g.lineStyle(6, cfg.accent, 1).strokeCircle(0, 0, NODE_R - 3);
      const parts: Phaser.GameObjects.GameObject[] = [g];
      if (st === "locked") {
        const lock = this.add.graphics();
        lock.lineStyle(5, 0x8a90bd, 1).beginPath().arc(0, -10, 11, Math.PI, 0).strokePath();
        lock.lineBetween(-11, -10, -11, -4).lineBetween(11, -10, 11, -4);
        lock.fillStyle(0x8a90bd, 1).fillRoundedRect(-16, -5, 32, 24, 6);
        lock.fillStyle(0x343a60, 1).fillCircle(0, 5, 4);
        parts.push(lock, this.add.text(0, 33, String(n), { fontFamily: FONT, fontSize: "15px", color: "#8a90bd", fontStyle: "bold" }).setOrigin(0.5));
      } else {
        const color = st === "current" ? "#1b1f3a" : st === "done" ? onAccent : "#ffffff";
        const fontSize = n >= 100 ? 30 : 36;
        parts.push(this.add.text(0, 1, String(n), { fontFamily: FONT, fontSize: `${fontSize}px`, color, fontStyle: "bold" }).setOrigin(0.5));
      }
      if (st === "done" || (st === "current" && stars > 0)) {
        for (let i = 0; i < 3; i++) {
          const sg = this.add.graphics().setName(`star-${i}`);
          drawStar(sg, 0, 0, 14, i < stars);
          sg.setPosition((i - 1) * 32, i === 1 ? -64 : -54);
          parts.push(sg);
        }
      }
      // Designed chapters (level 21+, @enjoyment/core designer): hard levels and the finale wear a tag.
      const role = designRole(n);
      if (role === "hard" || role === "boss") {
        const label = mapText(role, this.lang);
        const color = role === "boss" ? 0x9b5de5 : 0xff595e;
        const tag = this.add.text(0, NODE_R + 6, label, { fontFamily: FONT, fontSize: "15px", color: "#ffffff", fontStyle: "bold" }).setOrigin(0.5);
        const tg = this.add.graphics();
        const tw = tag.width + 18;
        tg.fillStyle(color, st === "locked" ? 0.7 : 1).fillRoundedRect(-tw / 2, NODE_R - 6, tw, 24, 12);
        g.lineStyle(5, color, st === "locked" ? 0.6 : 1).strokeCircle(0, 0, NODE_R + 3);
        parts.push(tg, tag);
      }
      if (st === "skipped") {
        const badge = this.add.graphics();
        badge.fillStyle(0x6a4c93, 1).fillRoundedRect(14, 22, 40, 26, 13);
        parts.push(badge, this.add.text(34, 35, "»", { fontFamily: FONT, fontSize: "22px", color: "#ffffff", fontStyle: "bold" }).setOrigin(0.5));
      }
      const c = this.add.container(p.x, p.y, parts).setSize(NODE_R * 2 + 24, NODE_R * 2 + 24).setInteractive({ useHandCursor: true });
      c.on("pointerup", () => {
        if (!this.moved) this.tapNode(n);
      });
      this.world.add(c);
      if (this.pin) this.world.bringToTop(this.pin);
      this.nodes.set(n, c);
      return c;
    }

    private placeMarker(n: number, animate: boolean): void {
      const p = nodePoint(n, size);
      if (!this.pulse) {
        this.pulse = this.add.circle(p.x, p.y, NODE_R + 4).setStrokeStyle(5, cfg.accent, 1);
        this.world.addAt(this.pulse, this.world.getIndex(this.overlayRoad) + 1);
        this.tweens.add({ targets: this.pulse, scale: 1.4, alpha: 0, duration: 1100, repeat: -1, ease: "Quad.Out" });
      }
      this.pulse.setPosition(p.x, p.y);
      if (!this.pin) {
        const g = this.add.graphics();
        g.fillStyle(0x000000, 0.25).fillEllipse(0, 50, 30, 10);
        g.fillStyle(shade(cfg.accent, 0.7), 1).fillCircle(0, 2, 27).fillTriangle(-18, 16, 18, 16, 0, 44);
        g.fillStyle(cfg.accent, 1).fillCircle(0, 0, 27).fillTriangle(-18, 12, 18, 12, 0, 40);
        g.fillStyle(0xffffff, 1).fillCircle(0, 0, 13);
        g.fillStyle(cfg.accent, 1).fillTriangle(-4, -7, -4, 7, 8, 0);
        const inner = this.add.container(0, 0, [g]);
        this.pin = this.add.container(p.x, p.y - PIN_DY, [inner]);
        this.world.add(this.pin);
        this.tweens.add({ targets: inner, y: -10, duration: 620, yoyo: true, repeat: -1, ease: "Sine.InOut" });
      }
      if (!animate) this.pin.setPosition(p.x, p.y - PIN_DY);
      this.world.bringToTop(this.pin);
    }

    private drawChrome(): void {
      const H = this.viewH;
      this.add.rectangle(W / 2, 0, W * 3, TOP_H, 0x12152b, 0.96).setOrigin(0.5, 0).setDepth(50).setInteractive();
      this.add.rectangle(W / 2, TOP_H, W * 3, 10, 0x000000, 0.22).setOrigin(0.5, 0).setDepth(50);
      text(this, 36, 44, pick(this.lang, cfg.title), 34).setOrigin(0, 0.5).setDepth(51);
      this.add
        .text(36, 98, cfg.back ? pick(this.lang, cfg.back.label) : mapText("games", this.lang), { fontFamily: FONT, fontSize: "22px", color: TEXT_DIM })
        .setOrigin(0, 0.5)
        .setDepth(51)
        .setInteractive({ useHandCursor: true })
        .on("pointerdown", (_p: Phaser.Input.Pointer, _x: number, _y: number, ev: Phaser.Types.Input.EventData) => {
          ev.stopPropagation();
          if (cfg.back) cfg.back.go(this);
          else location.href = new URLSearchParams(location.search).has("shell") ? "/" : "https://enjoyment.app/?utm_source=arcade";
        });
      this.add.text(W - 36, 30, mapText("stars", this.lang), { fontFamily: FONT, fontSize: "20px", color: TEXT_DIM, fontStyle: "bold" }).setOrigin(1, 0.5).setDepth(51);
      this.starTotal = this.add.text(W - 36, 88, "", { fontFamily: FONT, fontSize: "50px", color: "#ffd23f", fontStyle: "bold" }).setOrigin(1, 0.5).setDepth(51);
      // "☁ Kaydet": phone login + cloud save, shown only when the API offers it (never a gate; kit/account.ts).
      this.cloud = accountButton(this, W / 2 + 8, 98, { lang: this.lang, accent: cfg.accent, depth: 51 });

      this.bottomBg = this.add.rectangle(W / 2, H - BOTTOM_H, W * 3, BOTTOM_H, 0x12152b, 0.95).setOrigin(0.5, 0).setDepth(50).setInteractive();
      this.footer = text(this, W / 2, H - BOTTOM_H + 34, "", 20, "#aab1dd", false).setDepth(51);
      this.playBtn = button(this, W / 2, H - 98, 470, 104, "", cfg.accent, () => {
        if (!this.busy) this.openCard(this.progress.unlocked);
      }, onAccent).setDepth(51);
      this.refreshChrome();
    }

    private refreshChrome(): void {
      const n = this.view.unlocked;
      const c = chapterOf(n, size);
      const { got, max } = chapterStars(this.progress, c, size);
      this.starTotal.setText(`★ ${totalStars(this.progress)}`);
      this.footer.setText(`${mapText("chapter", this.lang)} ${c + 1} · ${pick(this.lang, theme(c))}  ·  ★ ${got}/${max}`);
      (this.playBtn.getAt(1) as Phaser.GameObjects.Text).setText(`${mapText("play", this.lang)}  ·  ${mapText("level", this.lang)} ${n}`);
    }

    private relayout(): void {
      if (!this.world) return;
      this.viewH = this.scale.height / this.cameras.main.zoom;
      this.bottomBg.setY(this.viewH - BOTTOM_H);
      this.footer.setY(this.viewH - BOTTOM_H + 34);
      this.playBtn.setY(this.viewH - 98);
      this.bounds();
      this.scrollTo(this.world.y);
      if (this.card) {
        const n = this.cardLevel;
        this.closeCard();
        this.openCard(n);
      }
    }

    // ---------------------------------------------------------------- scrolling

    private bounds(): void {
      const topWorld = chapterBand(chapterOf(this.last, size), size)[0] - 120;
      const bannerBottom = chapterBannerY(0, size) + 60;
      this.lo = this.viewH - BOTTOM_H - 20 - bannerBottom;
      this.hi = Math.max(this.lo, TOP_H - topWorld);
    }

    private scrollFor(n: number): number {
      const mid = (TOP_H + (this.viewH - BOTTOM_H)) / 2 + 40;
      return Phaser.Math.Clamp(mid - nodePoint(n, size).y, this.lo, this.hi);
    }

    private scrollTo(y: number): void {
      this.world.y = Phaser.Math.Clamp(y, this.lo, this.hi);
      this.cull();
    }

    private cull(): void {
      const H = this.viewH;
      for (const [, node] of this.nodes) {
        const y = node.y + this.world.y;
        node.setVisible(y > TOP_H - 120 && y < H - BOTTOM_H + 140);
      }
    }

    private onDown(p: Phaser.Input.Pointer): void {
      if (this.card) return;
      this.dragging = true;
      this.moved = false;
      this.vel = 0;
      this.downY = p.y;
      this.lastY = p.y;
      this.lastT = p.time;
      this.startScroll = this.world.y;
    }

    private onMove(p: Phaser.Input.Pointer): void {
      if (!this.dragging || !p.isDown) return;
      const z = this.cameras.main.zoom;
      const dy = (p.y - this.downY) / z;
      if (Math.abs(dy) > 12) this.moved = true;
      if (!this.moved) return;
      this.scrollTo(this.startScroll + dy);
      const dt = Math.max(1, p.time - this.lastT);
      this.vel = Phaser.Math.Linear(this.vel, (p.y - this.lastY) / z / dt, 0.5);
      this.lastY = p.y;
      this.lastT = p.time;
    }

    private onUp(): void {
      this.dragging = false;
    }

    private onKey(e: KeyboardEvent): void {
      if (e.key === "Enter" || e.key === " ") {
        if (this.card) this.play(this.cardLevel);
        else if (!this.busy) this.openCard(this.progress.unlocked);
      } else if (e.key === "Escape") this.closeCard();
      else if (e.key === "ArrowUp" || e.key === "PageUp") this.scrollTo(this.world.y + 320);
      else if (e.key === "ArrowDown" || e.key === "PageDown") this.scrollTo(this.world.y - 320);
    }

    // ---------------------------------------------------------------- level card

    private tapNode(n: number): void {
      if (this.busy || this.card) return;
      unlockAudio();
      if (nodeState(this.progress, n) === "locked") {
        sfx.nope();
        const node = this.nodes.get(n);
        if (node) this.tweens.add({ targets: node, x: node.x + 8, duration: 50, yoyo: true, repeat: 2 });
        const p = nodePoint(n, size);
        const toast = text(this, p.x, p.y - 80, mapText("locked", this.lang).replace("{n}", String(this.progress.unlocked)), 22, "#ffffff")
          .setStroke("#12152b", 8)
          .setDepth(5);
        this.world.add(toast);
        this.tweens.add({ targets: toast, y: toast.y - 40, alpha: 0, delay: 700, duration: 500, onComplete: () => toast.destroy() });
        return;
      }
      this.openCard(n);
    }

    private openCard(n: number): void {
      this.closeCard();
      unlockAudio();
      sfx.goalTick();
      this.cardLevel = n;
      const H = this.viewH;
      const cy = H / 2;
      const st = nodeState(this.progress, n);
      const stars = starsOf(this.progress, n);
      const c = this.add.container(0, 0).setDepth(100);
      const dim = this.add.rectangle(W / 2, H / 2, W * 3, H, 0x000000, 0.6).setInteractive();
      dim.on("pointerdown", () => this.closeCard());
      const panel = this.add.graphics();
      panel.fillStyle(0x000000, 0.35).fillRoundedRect(W / 2 - 296, cy - 266, 600, 540, 28);
      panel.fillStyle(PANEL, 1).fillRoundedRect(W / 2 - 300, cy - 270, 600, 540, 28);
      panel.fillStyle(cfg.accent, 1).fillRoundedRect(W / 2 - 300, cy - 270, 600, 14, { tl: 28, tr: 28, bl: 0, br: 0 });
      panel.lineStyle(3, 0x3a4170, 1).strokeRoundedRect(W / 2 - 300, cy - 270, 600, 540, 28);
      const swallow = this.add.zone(W / 2, cy, 600, 540).setInteractive();
      const chapter = chapterOf(n, size);
      const parts: Phaser.GameObjects.GameObject[] = [
        dim,
        panel,
        swallow,
        text(this, W / 2, cy - 214, `${mapText("chapter", this.lang)} ${chapter + 1} · ${pick(this.lang, theme(chapter)).toLocaleUpperCase(this.lang === "tr" ? "tr-TR" : "en-US")}`, 18, "#aab1dd"),
        text(this, W / 2, cy - 160, `${mapText("level", this.lang)} ${n}`, 58),
      ];
      const sg = this.add.graphics();
      for (let i = 0; i < 3; i++) drawStar(sg, W / 2 + (i - 1) * 84, cy - 66 + (i === 1 ? -12 : 0), i === 1 ? 34 : 29, i < stars);
      parts.push(sg);
      const role = designRole(n);
      const roleLine = role === "hard" ? mapText("hardCard", this.lang) : role === "boss" ? mapText("bossCard", this.lang) : "";
      const goal = [roleLine, cfg.describe?.(n, this.lang) ?? ""].filter(Boolean).join("\n");
      // Goal (one or two lines) flows down from under the stars; status lines follow it.
      let y = cy - 6;
      if (goal) {
        const g = text(this, W / 2, y, goal, 24, "#ffffff", false).setOrigin(0.5, 0).setWordWrapWidth(540);
        parts.push(g);
        y += g.height + 12;
      }
      const status = st === "skipped" ? mapText("skipped", this.lang) : st === "current" && !stars ? mapText("fresh", this.lang) : stars >= 3 ? mapText("perfect", this.lang) : mapText("more", this.lang);
      parts.push(text(this, W / 2, y, status, 21, st === "skipped" ? "#c9b6ff" : "#ffd23f", false).setOrigin(0.5, 0).setWordWrapWidth(540));
      parts.push(text(this, W / 2, y + 32, mapText("free", this.lang), 16, TEXT_DIM, false).setOrigin(0.5, 0).setWordWrapWidth(540));
      parts.push(button(this, W / 2 - 150, cy + 186, 240, 92, mapText("close", this.lang), PANEL_2, () => this.closeCard()));
      this.cardPlay = button(this, W / 2 + 130, cy + 186, 280, 92, st === "current" ? mapText("play", this.lang) : mapText("replay", this.lang), cfg.accent, () => this.play(n), onAccent);
      parts.push(this.cardPlay);
      c.add(parts);
      c.setAlpha(0);
      const inner = parts.slice(1);
      for (const o of inner) (o as unknown as { y: number }).y += 30;
      this.tweens.add({ targets: c, alpha: 1, duration: 160 });
      this.tweens.add({ targets: inner, y: "-=30", duration: 220, ease: "Back.Out" });
      this.card = c;
    }

    private closeCard(): void {
      this.card?.destroy();
      this.card = undefined;
      this.cardPlay = undefined;
    }

    private play(n: number): void {
      if (this.busy || nodeState(this.progress, n) === "locked") return;
      unlockAudio();
      track("map_play", { level: n, replay: n < this.progress.unlocked });
      this.scene.start(cfg.gameScene, { level: n });
    }

    // ---------------------------------------------------------------- celebration

    private tween(config: Phaser.Types.Tweens.TweenBuilderConfig): Promise<void> {
      return new Promise((resolve) => this.tweens.add({ ...config, onComplete: () => resolve() }));
    }

    private wait(ms: number): Promise<void> {
      return new Promise((resolve) => this.time.delayedCall(ms, resolve));
    }

    private async celebrate(r: MapReturn, unlock: boolean): Promise<void> {
      const n = r.from!;
      this.busy = true;
      await this.wait(260);
      // 1. the level just played turns into a finished node; new stars pop one by one
      const finalState: NodeState = r.result === "skip" && !starsOf(this.progress, n) ? "skipped" : "done";
      const stars = starsOf(this.progress, n);
      const prev = Math.min(stars, r.prevStars ?? 0);
      const node = this.makeNode(n, { state: finalState, stars });
      const fresh: Phaser.GameObjects.Graphics[] = [];
      for (let i = prev; i < stars; i++) {
        const s = node.getByName(`star-${i}`) as Phaser.GameObjects.Graphics | null;
        if (s) fresh.push(s.setScale(0));
      }
      this.tweens.add({ targets: node, scale: 1.12, duration: 120, yoyo: true, ease: "Quad.Out" });
      for (const s of fresh) {
        sfx.goalTick();
        await this.tween({ targets: s, scale: 1, duration: 240, ease: "Back.Out" });
      }
      if (!unlock) {
        this.view = this.progress;
        this.finishCelebration();
        return;
      }
      // 2. the road lights up to the next level and the pin walks along it
      await this.wait(120);
      const counter = { u: n };
      this.tweens.add({ targets: this.world, y: this.scrollFor(n + 1), duration: 700, ease: "Sine.InOut", onUpdate: () => this.cull() });
      await this.tween({
        targets: counter,
        u: n + 1,
        duration: 700,
        ease: "Sine.InOut",
        onUpdate: () => {
          this.overlayRoad.clear();
          this.strokeRoad(this.overlayRoad, n, counter.u, 28, ROAD, 1);
          const p = this.at(counter.u);
          this.pin?.setPosition(p.x, p.y - PIN_DY);
          this.pulse?.setPosition(p.x, p.y);
        },
      });
      // 3. the next level opens
      this.view = this.progress;
      const next = this.makeNode(n + 1);
      this.placeMarker(n + 1, false);
      next.setScale(0.6);
      sfx.special();
      this.burst(nodePoint(n + 1, size));
      await this.tween({ targets: next, scale: 1, duration: 320, ease: "Back.Out" });
      // 4. finishing a chapter is a moment of its own
      if (chapterOf(n, size) !== chapterOf(n + 1, size)) {
        sfx.win();
        confetti(this);
        (this.world.getByName(`banner-${chapterOf(n + 1, size)}`) as Phaser.GameObjects.Container | null)?.setAlpha(1);
        const label = text(this, W / 2, this.viewH / 2 - 120, mapText("chapterDone", this.lang), 58, "#ffd23f").setStroke("#12152b", 10).setDepth(90).setScale(0.4);
        await this.tween({ targets: label, scale: 1, duration: 260, ease: "Back.Out" });
        this.tweens.add({ targets: label, alpha: 0, y: label.y - 60, delay: 900, duration: 400, onComplete: () => label.destroy() });
        await this.wait(700);
      }
      this.finishCelebration();
      this.time.delayedCall(260, () => {
        if (!this.card) this.openCard(this.progress.unlocked);
      });
    }

    private finishCelebration(): void {
      this.busy = false;
      this.cull();
      this.refreshChrome();
    }

    private burst(p: { x: number; y: number }): void {
      for (let i = 0; i < 12; i++) {
        const a = (i / 12) * Math.PI * 2;
        const dot = this.add.circle(p.x, p.y, 6, i % 2 ? GOLD : cfg.accent);
        this.world.add(dot);
        this.tweens.add({ targets: dot, x: p.x + Math.cos(a) * 90, y: p.y + Math.sin(a) * 90, alpha: 0, scale: 0.3, duration: 520, ease: "Quad.Out", onComplete: () => dot.destroy() });
      }
    }

    // ---------------------------------------------------------------- bot hook

    private botState(): Record<string, unknown> {
      // The game may still be loading after the map handed over; its own hook replaces this one.
      if (!this.sys.isActive()) return { scene: "map-closed" };
      const cam = this.cameras.main;
      const css = this.game.canvas.getBoundingClientRect().width / this.scale.width;
      const pt = (x: number, y: number) => ({ x: (x - cam.scrollX) * cam.zoom * css, y: (y - cam.scrollY) * cam.zoom * css });
      const nodes: Record<string, { x: number; y: number; state: NodeState; visible: boolean }> = {};
      for (const [n, node] of this.nodes) nodes[n] = { ...pt(node.x + this.world.x, node.y + this.world.y), state: nodeState(this.view, n), visible: node.visible };
      return {
        scene: "map",
        unlocked: this.progress.unlocked,
        stars: totalStars(this.progress),
        busy: this.busy,
        card: this.card ? this.cardLevel : 0,
        last: this.last,
        scroll: this.world.y,
        account: pageAccount()?.current.status ?? "off",
        screen: {
          play: pt(this.playBtn.x, this.playBtn.y),
          cardPlay: this.cardPlay ? pt(this.cardPlay.x, this.cardPlay.y) : null,
          account: this.cloud.visible ? pt(this.cloud.x, this.cloud.y) : null,
          nodes,
        },
      };
    }
  };
}
